/**
 * 플랫폼 사용자 엑셀 일괄 등록 — 파싱·검증·결과 다운로드.
 * 서버는 파일이 아닌 JSON `{ users: [...] }` 를 받으며, 최대 500건·전체 성공/실패이다.
 */
import * as XLSX from 'xlsx';
import type {
  PlatformUserBulkRegisterItem,
  PlatformUserBulkRegisterResultUser,
  PromoContractType,
} from '@/entities/user/api/user.api';
import {
  downloadXlsx,
  exportXlsxTemplateWithListValidations,
  importFromXlsx,
} from '@/shared/utils/xlsxExport';

export const USER_BULK_MAX_COUNT = 500;

/** 엑셀 콤보박스 표시값. 비어 있으면 서버가 INTERNAL(계약 필요)로 처리 */
export const USER_BULK_PROMO_CONTRACT_EXCEL_OPTIONS = ['내부', '외부'] as const;

/** 엑셀 컬럼 ↔ API 필드 매핑. promoContractType 만 선택 */
export const USER_BULK_UPLOAD_HEADER_MAP: Record<
  keyof PlatformUserBulkRegisterItem,
  string
> = {
  email: '계정 이메일',
  phoneNumber: '전화번호',
  promoLicenseNumber: '판촉영업자신고번호',
  businessLicenseNumber: '사업자등록번호',
  companyName: '업체명',
  ceoName: '대표자명',
  address: '주소',
  taxInvoiceEmail: '세금계산서이메일',
  bankName: '금융기관',
  bankAccountNumber: '계좌번호',
  promoContractType: '위탁계약서유형',
};

export type UserBulkFieldKey = keyof PlatformUserBulkRegisterItem;

export const USER_BULK_REQUIRED_FIELDS: UserBulkFieldKey[] = (
  Object.keys(USER_BULK_UPLOAD_HEADER_MAP) as UserBulkFieldKey[]
).filter((field) => field !== 'promoContractType');

export const USER_BULK_RESULT_HEADER_MAP: Record<string, string> = {
  userId: '유저 ID',
  email: '계정 이메일',
  temporaryPassword: '임시 비밀번호',
};

export interface UserBulkValidationError {
  /** 엑셀 행 번호 (헤더 1행 기준, 2부터 데이터) */
  row: number;
  field: UserBulkFieldKey;
  fieldLabel: string;
  message: string;
}

export interface UserBulkParseResult {
  users: PlatformUserBulkRegisterItem[];
  errors: UserBulkValidationError[];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^010-?\d{4}-?\d{4}$/;
const BUSINESS_LICENSE_PATTERN = /^\d{3}-?\d{2}-?\d{5}$/;

const MAX_LENGTH: Partial<Record<UserBulkFieldKey, number>> = {
  email: 255,
  promoLicenseNumber: 100,
  companyName: 255,
  ceoName: 100,
  address: 500,
  taxInvoiceEmail: 255,
  bankName: 100,
  bankAccountNumber: 100,
};

export type PromoContractTypeParseResult =
  | { ok: true; value?: PromoContractType }
  | { ok: false };

/** 엑셀 위탁계약서유형 → API promoContractType. 빈 값은 생략(서버 INTERNAL) */
export const parsePromoContractType = (
  raw: string,
): PromoContractTypeParseResult => {
  const value = raw.trim();
  if (value === '') return { ok: true };
  const normalized = value.toUpperCase();
  if (value === '외부' || normalized === 'EXTERNAL') {
    return { ok: true, value: 'EXTERNAL' };
  }
  if (value === '내부' || normalized === 'INTERNAL') {
    return { ok: true, value: 'INTERNAL' };
  }
  return { ok: false };
};

const cellToString = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const asInt = Math.trunc(value);
    const str = String(asInt);
    // Excel이 숫자로 저장한 휴대폰 번호(선행 0 누락) 보정
    if (str.length === 10 && str.startsWith('10')) {
      return `0${str}`;
    }
    return str;
  }
  return String(value).trim();
};

const isRowEmpty = (row: Record<string, unknown>): boolean =>
  Object.keys(USER_BULK_UPLOAD_HEADER_MAP).every(
    (key) => cellToString(row[key]) === '',
  );

export const filterNonEmptyUserBulkRows = (
  rows: Record<string, unknown>[],
): Record<string, unknown>[] => rows.filter((row) => !isRowEmpty(row));

const mapRowToUser = (
  row: Record<string, unknown>,
): {
  user: PlatformUserBulkRegisterItem;
  contractParse: PromoContractTypeParseResult;
} => {
  const contractParse = parsePromoContractType(
    cellToString(row.promoContractType),
  );
  const user: PlatformUserBulkRegisterItem = {
    email: cellToString(row.email),
    phoneNumber: cellToString(row.phoneNumber),
    promoLicenseNumber: cellToString(row.promoLicenseNumber),
    businessLicenseNumber: cellToString(row.businessLicenseNumber),
    companyName: cellToString(row.companyName),
    ceoName: cellToString(row.ceoName),
    address: cellToString(row.address),
    taxInvoiceEmail: cellToString(row.taxInvoiceEmail),
    bankName: cellToString(row.bankName),
    bankAccountNumber: cellToString(row.bankAccountNumber),
  };

  // 빈 값은 보내지 않는다. 서버 기본값 INTERNAL(계약 필요)과 동일하다.
  if (contractParse.ok && contractParse.value) {
    user.promoContractType = contractParse.value;
  }

  return { user, contractParse };
};

const pushRequiredError = (
  errors: UserBulkValidationError[],
  row: number,
  field: UserBulkFieldKey,
  value: string,
) => {
  if (value !== '') return;
  errors.push({
    row,
    field,
    fieldLabel: USER_BULK_UPLOAD_HEADER_MAP[field],
    message: `${USER_BULK_UPLOAD_HEADER_MAP[field]}은(는) 필수입니다.`,
  });
};

const pushMaxLengthError = (
  errors: UserBulkValidationError[],
  row: number,
  field: UserBulkFieldKey,
  value: string,
) => {
  const max = MAX_LENGTH[field];
  if (max == null || value.length <= max) return;
  errors.push({
    row,
    field,
    fieldLabel: USER_BULK_UPLOAD_HEADER_MAP[field],
    message: `${USER_BULK_UPLOAD_HEADER_MAP[field]}은(는) ${max}자 이하여야 합니다.`,
  });
};

/** 프론트 1차 검증 — 필수값·형식·이메일/사업자등록번호 중복·건수 제한 */
export const validateUserBulkItems = (
  users: PlatformUserBulkRegisterItem[],
  startRow = 2,
): UserBulkValidationError[] => {
  const errors: UserBulkValidationError[] = [];

  if (users.length === 0) {
    errors.push({
      row: 0,
      field: 'email',
      fieldLabel: '전체',
      message: '등록할 사용자 데이터가 없습니다.',
    });
    return errors;
  }

  if (users.length > USER_BULK_MAX_COUNT) {
    errors.push({
      row: 0,
      field: 'email',
      fieldLabel: '전체',
      message: `한 번에 최대 ${USER_BULK_MAX_COUNT}건까지 등록할 수 있습니다.`,
    });
    return errors;
  }

  const emailRows = new Map<string, number>();
  // 서버는 파일 내 번호 중복 시 행 정보 없이 전체를 거절하므로 여기서 행을 짚어 준다.
  // 서버와 같이 하이픈 유무는 무시하고 숫자만 비교한다.
  const businessLicenseRows = new Map<string, number>();

  users.forEach((user, index) => {
    const row = startRow + index;

    USER_BULK_REQUIRED_FIELDS.forEach((field) => {
      const value = String(user[field] ?? '');
      pushRequiredError(errors, row, field, value);
      pushMaxLengthError(errors, row, field, value);
    });

    if (user.email) {
      if (!EMAIL_PATTERN.test(user.email)) {
        errors.push({
          row,
          field: 'email',
          fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.email,
          message: '유효한 계정 이메일 형식이 아닙니다.',
        });
      }
      const normalizedEmail = user.email.toLowerCase();
      const duplicateRow = emailRows.get(normalizedEmail);
      if (duplicateRow != null) {
        errors.push({
          row,
          field: 'email',
          fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.email,
          message: `${duplicateRow}행과 이메일이 중복됩니다.`,
        });
      } else {
        emailRows.set(normalizedEmail, row);
      }
    }

    if (user.phoneNumber && !PHONE_PATTERN.test(user.phoneNumber)) {
      errors.push({
        row,
        field: 'phoneNumber',
        fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.phoneNumber,
        message: '전화번호는 01012345678 또는 010-1234-5678 형식이어야 합니다.',
      });
    }

    if (
      user.businessLicenseNumber &&
      !BUSINESS_LICENSE_PATTERN.test(user.businessLicenseNumber)
    ) {
      errors.push({
        row,
        field: 'businessLicenseNumber',
        fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.businessLicenseNumber,
        message:
          '사업자등록번호는 1234567890 또는 123-45-67890 형식이어야 합니다.',
      });
    }

    if (user.businessLicenseNumber) {
      const digits = user.businessLicenseNumber.replace(/\D/g, '');
      const duplicateRow = businessLicenseRows.get(digits);
      if (duplicateRow != null) {
        errors.push({
          row,
          field: 'businessLicenseNumber',
          fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.businessLicenseNumber,
          message: `${duplicateRow}행과 사업자등록번호가 중복됩니다.`,
        });
      } else if (digits) {
        businessLicenseRows.set(digits, row);
      }
    }

    if (user.taxInvoiceEmail && !EMAIL_PATTERN.test(user.taxInvoiceEmail)) {
      errors.push({
        row,
        field: 'taxInvoiceEmail',
        fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.taxInvoiceEmail,
        message: '유효한 세금계산서 이메일 형식이 아닙니다.',
      });
    }
  });

  return errors;
};

export const mapExcelRowsToBulkUsers = (
  rows: Record<string, unknown>[],
  startRow = 2,
): UserBulkParseResult => {
  const users: PlatformUserBulkRegisterItem[] = [];
  const contractErrors: UserBulkValidationError[] = [];

  rows.forEach((row, index) => {
    const { user, contractParse } = mapRowToUser(row);
    users.push(user);
    if (!contractParse.ok) {
      contractErrors.push({
        row: startRow + index,
        field: 'promoContractType',
        fieldLabel: USER_BULK_UPLOAD_HEADER_MAP.promoContractType,
        message: '위탁계약서유형은 비워 두거나 내부, 외부를 입력해야 합니다.',
      });
    }
  });

  return {
    users,
    errors: [...contractErrors, ...validateUserBulkItems(users)],
  };
};

export const parseUserBulkUploadFile = async (
  file: File,
): Promise<UserBulkParseResult> => {
  const rawRows = await importFromXlsx(file, USER_BULK_UPLOAD_HEADER_MAP);
  const nonEmptyRows = filterNonEmptyUserBulkRows(rawRows);
  return mapExcelRowsToBulkUsers(nonEmptyRows);
};

export const downloadUserBulkUploadTemplate = () => {
  const columnIndex = (
    Object.keys(USER_BULK_UPLOAD_HEADER_MAP) as UserBulkFieldKey[]
  ).indexOf('promoContractType');
  const column = XLSX.utils.encode_col(columnIndex);

  return exportXlsxTemplateWithListValidations(
    USER_BULK_UPLOAD_HEADER_MAP,
    '플랫폼_사용자_일괄등록_양식.xlsx',
    [
      {
        sqref: `${column}2:${column}${USER_BULK_MAX_COUNT + 1}`,
        options: [...USER_BULK_PROMO_CONTRACT_EXCEL_OPTIONS],
        allowBlank: true,
        promptTitle: '위탁계약서유형',
        promptMessage:
          '내부(계약 필요) 또는 외부를 선택하세요. 비워 두면 내부로 등록됩니다.',
        errorTitle: '입력 오류',
        errorMessage: '내부 또는 외부를 선택하세요.',
      },
    ],
  );
};

export const downloadUserBulkRegisterResultXlsx = (
  users: PlatformUserBulkRegisterResultUser[],
  filename = '플랫폼_사용자_일괄등록_결과.xlsx',
) => {
  const rows = users.map((user) => ({
    [USER_BULK_RESULT_HEADER_MAP.userId]: user.userId,
    [USER_BULK_RESULT_HEADER_MAP.email]: user.email,
    [USER_BULK_RESULT_HEADER_MAP.temporaryPassword]: user.temporaryPassword,
  }));

  downloadXlsx(rows, filename);
};

/** 서버 `errors[].field` (예: users[0].email) → 엑셀 행·필드 */
export const mapServerBulkRegisterErrors = (
  apiErrors: Array<{ field?: string; message?: string }>,
  startRow = 2,
): UserBulkValidationError[] =>
  apiErrors.flatMap((item) => {
    const fieldPath = item.field ?? '';
    const match = fieldPath.match(/^users\[(\d+)\]\.(\w+)$/);
    if (!match) {
      return [
        {
          row: 0,
          field: 'email' as UserBulkFieldKey,
          fieldLabel: '전체',
          message: item.message ?? '등록에 실패했습니다.',
        },
      ];
    }

    const rowIndex = Number(match[1]);
    const fieldKey = match[2] as UserBulkFieldKey;
    const fieldLabel = USER_BULK_UPLOAD_HEADER_MAP[fieldKey] ?? fieldKey;

    return [
      {
        row: startRow + rowIndex,
        field: fieldKey,
        fieldLabel,
        message: item.message ?? '입력값을 확인해주세요.',
      },
    ];
  });

export type ApiBulkRegisterErrorBody = {
  code?: string;
  message?: string;
  errors?: Array<{ field?: string; message?: string }>;
};

export const extractApiBulkRegisterErrorBody = (
  error: unknown,
): ApiBulkRegisterErrorBody | null => {
  const err = error as { response?: { data?: ApiBulkRegisterErrorBody } };
  return err?.response?.data ?? null;
};
